import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { Paginator } from '@/components/Paginator/Paginator';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { useNavigate, useParams } from 'react-router';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationUsersTable } from './ui/OrganizationUsersTable';
import { useState } from 'react';
import { Drawer } from '@/components/Drawer/Drawer';
import { CreateOrganizationAdminForm } from './ui/CreateOrganizationAdminForm/CreateOrganizationAdminForm';
import { getOpcoOrganizationsPath } from '../../path/path';
import { Button } from '@/components/Button/Button';
import PlusIcon from '../../../../images/PlusIcon.svg?react';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { ORGANIZATION_STATUS } from '@/bundle/_Opco/types/types';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { EditOrganizationForm } from '@/bundle/_Opco/ui/EditOrganizationForm/EditOrganizationForm';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { OrganizationDetailsPanel } from './ui/OrganizationDetailsPanel';
import { getListData, showSnackbarResponseError } from '@/helpers/helpers';
import { DEFAULT_ORGANIZATION_USER_LIMIT, isUsersLimitReached } from '@/helpers/billingHelpers';
import { UserLimitReachedModal } from '@/bundle/shared/components/UserLimitReachedModal/UserLimitReachedModal';
import { SUBSCRIPTION_PLAN_TYPE } from '@/const/shared';
import { RoundedButton } from '@/components/RoundedButton/RoundedButton';
import RightArrowIcon from '@/images/RightArrowIcon.svg?react';
import { getOpcoOrganizationMasterServiceAgreementPagePath } from '@/bundle/_Opco/ui/OrganizationMasterServiceAgreementPage/path';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { loadOrganization, updateOrganization } from '@/bundle/_Opco/api/api';
import { loadOrganizationUsers } from '@/bundle/_Opco/ui/OrganizationDetails/api/api';
import { isBlockedOrganizationStatus } from '@/helpers/organizationHelpers';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';
import { InitialUserLimitReachedModal } from './ui/InitialUserLimitReachedModal';

export const OrganizationDetailsPage = () => {
  const navigate = useNavigate();
  const { navigateBack } = useStatefulNavigation();
  const searchParams = useGetSearchParams();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [openUsersLimitModal, setOpenUsersLimitModal] = useState(false);
  const [openInitialUsersLimitModal, setOpenInitialUsersLimitModal] = useState(false);

  const { data: organizationData } = useQuery({
    queryKey: ['load_organization', id],
    queryFn: () => loadOrganization(id),
  });

  const { data: organizationUsersData, isPending: isPendingOrganizationUsers } = useQuery({
    queryKey: ['load_organization_users', id, searchParams],
    queryFn: () => loadOrganizationUsers(id, searchParams),
    placeholderData: keepPreviousData,
  });

  const organization = organizationData?.body;
  const isBlockedOrganization = isBlockedOrganizationStatus(organization?.status);
  const isEnterprisePlan = organization?.subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.ENTERPRISE;
  const hasSubscription =
    (organization?.subscription_plan_type === SUBSCRIPTION_PLAN_TYPE.STANDARD && !!organization?.billing_profile) ||
    isEnterprisePlan;
  const hasInitialUsersLimitReached =
    !hasSubscription && organization?.active_users_count >= DEFAULT_ORGANIZATION_USER_LIMIT;

  const { mutate, isPending } = useMutation({
    mutationKey: ['change_organization_status'],
    mutationFn: () => {
      const payload = {
        status: isBlockedOrganization ? ORGANIZATION_STATUS.ACTIVE : ORGANIZATION_STATUS.BLOCKED,
      };

      return updateOrganization(organization?.id, payload);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.EditOrganizationSuccess);

      queryClient.invalidateQueries({ queryKey: ['load_organization', id] });
      setIsOpenConfirmModal(false);
    },
  });

  const closeModal = () => {
    setIsOpen(false);
  };

  const closeEditDrawer = () => setIsEditOpen(false);

  const openEditDrawer = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.EditOrganizationRedirect);
    setIsEditOpen(true);
  };

  const createUserSuccess = () => {
    setIsOpen(false);
    queryClient.invalidateQueries({ queryKey: ['load_organization_users', id, searchParams] });
    queryClient.invalidateQueries({ queryKey: ['load_organization', id] });
  };

  const openCreateUserForm = () => {
    if (hasInitialUsersLimitReached) {
      setOpenInitialUsersLimitModal(true);

      return;
    }

    if (isUsersLimitReached(organization?.active_users_count, organization?.billing_profile?.current_plan?.max_users)) {
      setOpenUsersLimitModal(true);

      return;
    }

    setIsOpen(true);
    amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrganizationUserRedirect);
  };

  const editOrganizationSuccess = () => {
    setIsEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ['load_organization', id] });
  };

  const toggleBlockHeader = isBlockedOrganization ? 'Unblock Organization' : 'Block Organization';
  const toggleBlockMessage = isBlockedOrganization
    ? 'All users of this organization will regain access to their accounts, and the organization will be able to resume using WireVault.'
    : 'All users of this organization will lose access to their accounts, and the organization will no longer be able to use WireVault.';
  const showMasterServiceAgreementButton = organization?.is_msa_confirmed;
  const showMasterServiceAgreementTooltip = isEnterprisePlan && showMasterServiceAgreementButton;

  const { list, totalCount, hasPagination } = getListData(organizationUsersData?.body, searchParams);

  return (
    <ContentLayout
      title='Organization Details'
      breadcrumbs='Organizations > Details'
      backButton='Back to Org Management'
      onBack={() => navigateBack(getOpcoOrganizationsPath())}
      headerAction={
        <>
          <Button
            variant='secondary'
            width={160}
            isLoading={isPending}
            onClick={() => setIsOpenConfirmModal(true)}
            mobileStretch
          >
            {isBlockedOrganization ? 'Unblock' : 'Block'}
          </Button>
          {showMasterServiceAgreementButton && (
            <>
              {showMasterServiceAgreementTooltip && (
                <Tooltip id='enterprise-msa-tooltip' place='bottom-start'>
                  MSA from previous Standard plan subscription
                </Tooltip>
              )}
              <RoundedButton
                data-tooltip-id='enterprise-msa-tooltip'
                onClick={() => navigate(getOpcoOrganizationMasterServiceAgreementPagePath(id))}
                icon={<RightArrowIcon />}
              >
                View MSA
              </RoundedButton>
            </>
          )}
        </>
      }
    >
      <PageBody>
        <Box display='flex' height='100%' flexDirection='column' rowGap='56px'>
          {organization && <OrganizationDetailsPanel organization={organization} onEdit={openEditDrawer} />}
          <Box display='flex' flexDirection='column' height='100%' rowGap='24px'>
            <Box display='flex' flexDirection='column' rowGap='8px'>
              <Box display='flex' width='100%' alignItems='center' justifyContent='space-between'>
                <Typography variant='headline4' color={COLORS.black} fontWeight='medium'>
                  Users
                </Typography>
                <Button
                  width={143}
                  variant='primary'
                  size='medium'
                  iconPosition='left'
                  icon={<PlusIcon />}
                  onClick={openCreateUserForm}
                  disabled={isBlockedOrganization}
                >
                  Create User
                </Button>
              </Box>
              <Typography variant='bodySmall' color={COLORS.grey[700]}>
                View the list of users of the organization. Click on a particular row to view the data for the
                particular User.
              </Typography>
            </Box>
            <OrganizationUsersTable items={list} isLoading={isPendingOrganizationUsers} />
            {hasPagination && (
              <PageFooter>
                <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
              </PageFooter>
            )}
          </Box>
        </Box>
      </PageBody>

      <Drawer
        isOpen={isOpen}
        onClose={closeModal}
        header='Create User'
        subHeader='Please provide information for the new user.'
      >
        <CreateOrganizationAdminForm
          hasUsers={!!totalCount}
          onClose={closeModal}
          onSuccess={createUserSuccess}
          hasSubscription={hasSubscription}
          onOpenInitialUsersLimitModal={() => setOpenInitialUsersLimitModal(true)}
        />
      </Drawer>
      <Drawer
        isOpen={isEditOpen}
        header='Edit Organization'
        subHeader='Edit your organization details.'
        onClose={closeEditDrawer}
      >
        <EditOrganizationForm
          organization={organization}
          onSuccess={editOrganizationSuccess}
          onCancel={closeEditDrawer}
        />
      </Drawer>
      <ConfirmModal
        isLoading={isPending}
        isOpen={isOpenConfirmModal}
        header={toggleBlockHeader}
        body={toggleBlockMessage}
        onClose={() => setIsOpenConfirmModal(false)}
        onConfirm={mutate}
      />
      <UserLimitReachedModal isOpen={openUsersLimitModal} onClose={() => setOpenUsersLimitModal(false)} />
      <InitialUserLimitReachedModal
        isOpen={openInitialUsersLimitModal}
        onClose={() => setOpenInitialUsersLimitModal(false)}
      />
    </ContentLayout>
  );
};
